import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import AdminLayout from "@/components/AdminLayout";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Card, CardContent, CardHeader, CardTitle,
} from "@/components/ui/card";
import {
  Plus, Search, Briefcase, AlertCircle, CheckCircle2, PauseCircle, XCircle,
  FileEdit, Clock, CalendarClock, TrendingUp,
} from "lucide-react";

const STATUS_STYLES = {
  draft: "bg-slate-200 text-slate-700 border-slate-300",
  active: "bg-emerald-100 text-emerald-800 border-emerald-200",
  on_hold: "bg-amber-100 text-amber-800 border-amber-200",
  completed: "bg-sky-100 text-sky-800 border-sky-200",
  cancelled: "bg-rose-100 text-rose-700 border-rose-200",
};

const PRIORITY_STYLES = {
  low: "text-slate-500",
  medium: "text-slate-800",
  high: "text-orange-600 font-medium",
  urgent: "text-rose-600 font-semibold",
};

const StatusBadge = ({ status }) => (
  <span
    data-testid={`project-status-${status}`}
    className={`inline-block text-[10px] uppercase tracking-wider px-2 py-1 border rounded ${STATUS_STYLES[status] || STATUS_STYLES.draft}`}
  >
    {status?.replace("_", " ") || "draft"}
  </span>
);

const TABS = [
  { key: "all", label: "All", icon: Briefcase },
  { key: "active", label: "Active", icon: TrendingUp },
  { key: "on_hold", label: "On Hold", icon: PauseCircle },
  { key: "completed", label: "Completed", icon: CheckCircle2 },
  { key: "cancelled", label: "Cancelled", icon: XCircle },
  { key: "due_today", label: "Due Today", icon: CalendarClock },
  { key: "overdue", label: "Overdue", icon: AlertCircle },
  { key: "recently_updated", label: "Recently Updated", icon: Clock },
];

const StatCard = ({ tabKey, label, count, Icon, active, onClick }) => (
  <button
    data-testid={`project-tab-${tabKey}`}
    onClick={onClick}
    className={`text-left rounded-lg border p-4 transition-all ${active ? "border-slate-900 bg-slate-900 text-white shadow-md" : "bg-white hover:border-slate-400"}`}
  >
    <div className="flex items-center justify-between mb-2">
      <Icon className={`h-4 w-4 ${active ? "text-white" : "text-slate-500"}`} />
      <span className={`text-2xl font-semibold ${active ? "text-white" : "text-slate-900"}`}>{count ?? 0}</span>
    </div>
    <div className={`text-xs uppercase tracking-wider ${active ? "text-slate-200" : "text-slate-600"}`}>{label}</div>
  </button>
);

const AdminProjects = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [tab, setTab] = useState(searchParams.get("tab") || "all");
  const [q, setQ] = useState(searchParams.get("q") || "");
  const [debouncedQ, setDebouncedQ] = useState(q);
  const [priority, setPriority] = useState(searchParams.get("priority") || "any");
  const [dashboard, setDashboard] = useState({});
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q), 400);
    return () => clearTimeout(t);
  }, [q]);

  const loadDashboard = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/projects/dashboard");
      setDashboard(data);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn("[Projects dashboard] failed:", err?.response?.data);
    }
  }, []);

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (tab === "active" || tab === "on_hold" || tab === "completed" || tab === "cancelled") {
        params.status = tab;
      }
      if (tab === "due_today") params.due_filter = "today";
      if (tab === "overdue") params.due_filter = "overdue";
      if (tab === "recently_updated") params.due_filter = "recent";
      if (priority && priority !== "any") params.priority = priority;
      if (debouncedQ) params.q = debouncedQ;
      params.limit = 100;
      const { data } = await api.get("/admin/projects", { params });
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (err) {
      toast.error("Failed to load projects", {
        description: err?.response?.data?.detail || "Please try again",
      });
    } finally {
      setLoading(false);
    }
  }, [tab, priority, debouncedQ]);

  useEffect(() => { loadDashboard(); }, [loadDashboard]);
  useEffect(() => { loadList(); }, [loadList]);

  useEffect(() => {
    const next = new URLSearchParams();
    if (tab !== "all") next.set("tab", tab);
    if (q) next.set("q", q);
    if (priority && priority !== "any") next.set("priority", priority);
    setSearchParams(next, { replace: true });
  }, [tab, q, priority, setSearchParams]);

  const handleQuickCreate = async () => {
    setCreating(true);
    try {
      const { data } = await api.post("/admin/projects", {
        title: "Untitled project",
        service_type: "custom_design",
        priority: "medium",
        status: "draft",
      });
      toast.success("Project created", { description: data.project_number });
      navigate(`/admin/projects/${data.id}`);
    } catch (err) {
      toast.error("Could not create project", {
        description: err?.response?.data?.detail || "Please try again",
      });
    } finally {
      setCreating(false);
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-projects-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight" data-testid="projects-page-title">Project Management</h1>
            <p className="text-sm text-muted-foreground mt-1">Operating dashboard for all client engagements.</p>
          </div>
          <Button data-testid="new-project-btn" onClick={handleQuickCreate} disabled={creating}>
            <Plus className="h-4 w-4 mr-1.5" />
            {creating ? "Creating…" : "New Project"}
          </Button>
        </div>

        {/* Dashboard tabs */}
        <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-3" data-testid="projects-dashboard-grid">
          {TABS.map((t) => (
            <StatCard
              key={t.key}
              tabKey={t.key}
              label={t.label}
              count={dashboard[t.key]}
              Icon={t.icon}
              active={tab === t.key}
              onClick={() => setTab(t.key)}
            />
          ))}
        </div>

        {/* Filters */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex flex-col md:flex-row md:items-center gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  data-testid="projects-search-input"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search by project number, title, client…"
                  className="pl-9"
                />
              </div>
              <Select value={priority} onValueChange={setPriority}>
                <SelectTrigger data-testid="projects-priority-filter" className="w-full md:w-40">
                  <SelectValue placeholder="Priority" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">Any priority</SelectItem>
                  <SelectItem value="urgent">Urgent</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="low">Low</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardHeader>
          <CardContent className="pt-0" data-testid="projects-list">
            {loading ? (
              <div className="py-10 text-center text-sm text-muted-foreground">Loading projects…</div>
            ) : items.length === 0 ? (
              <div className="py-10 text-center text-sm text-muted-foreground" data-testid="projects-empty-state">
                No projects match this filter. <button onClick={handleQuickCreate} className="underline">Create the first one</button>.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-xs uppercase tracking-wider text-muted-foreground border-b">
                    <tr>
                      <th className="text-left py-3 pr-3">Project</th>
                      <th className="text-left py-3 pr-3">Client</th>
                      <th className="text-left py-3 pr-3">Service</th>
                      <th className="text-left py-3 pr-3">Status</th>
                      <th className="text-left py-3 pr-3">Priority</th>
                      <th className="text-left py-3 pr-3">Due</th>
                      <th className="text-left py-3 pr-3">Updated</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((p) => (
                      <tr key={p.id} data-testid={`project-row-${p.id}`} className="border-b last:border-b-0 hover:bg-slate-50">
                        <td className="py-3 pr-3">
                          <Link to={`/admin/projects/${p.id}`} className="font-medium hover:underline" data-testid={`project-link-${p.id}`}>
                            {p.title || "(untitled)"}
                          </Link>
                          <div className="text-xs text-muted-foreground">{p.project_number}</div>
                        </td>
                        <td className="py-3 pr-3">
                          <div>{p.client_name || "—"}</div>
                          <div className="text-xs text-muted-foreground">{p.client_email || ""}</div>
                        </td>
                        <td className="py-3 pr-3 capitalize">{(p.service_type || "").replace(/_/g, " ")}</td>
                        <td className="py-3 pr-3"><StatusBadge status={p.status} /></td>
                        <td className={`py-3 pr-3 capitalize ${PRIORITY_STYLES[p.priority] || ""}`}>{p.priority || "medium"}</td>
                        <td className="py-3 pr-3">{p.due_date || "—"}</td>
                        <td className="py-3 pr-3 text-xs text-muted-foreground">{p.updated_at ? p.updated_at.slice(0, 10) : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="pt-3 text-xs text-muted-foreground">{items.length} of {total} project{total === 1 ? "" : "s"}</div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default AdminProjects;
